/**
 * Signed URL Utilities V2 - Enhanced with Intelligent Caching
 * 
 * This file provides improved versions of the signed URL utilities that use
 * the existing API infrastructure with intelligent caching to reduce redundant 
 * API calls and improve performance.
 */

import { apiClient } from '../apiTools/apiClient';
import { config } from '../constants/config';
import { fetchUserSignedUrlPath } from './fetchSignedUrlByFolder';
import { buildS3Url } from './pathRegistryUtils';

// ===== INTELLIGENT CACHING SYSTEM =====

/**
 * Cache structure for signed URLs with expiration tracking
 */
class SignedUrlCache {
  constructor() {
    this.cache = new Map();
    this.SERVER_EXPIRATION_TIME = 3600; // 3600 seconds (1 hour) - matches server
    this.PRE_EXPIRATION_BUFFER = 300; // 5 minutes buffer before expiration
    this.CLEANUP_INTERVAL = 600000; // 10 minutes cleanup interval

    // Start periodic cleanup
    this.startCleanupTimer();
  }

  /**
   * Generate cache key from userId, urlPath, and operation
   */
  getCacheKey(userId, urlPath, operation = 'GET') {
    return `${userId}:${urlPath}:${operation}`;
  }

  /**
   * Check if a cached entry is expired or about to expire
   */
  isExpired(cacheEntry) {
    const now = Date.now();
    const expirationTime = cacheEntry.timestamp + (this.SERVER_EXPIRATION_TIME * 1000);
    const preExpirationTime = expirationTime - (this.PRE_EXPIRATION_BUFFER * 1000);

    return now >= preExpirationTime;
  }

  /**
   * Check if a cached entry is completely expired (past server expiration)
   */
  isCompletelyExpired(cacheEntry) {
    const now = Date.now();
    const expirationTime = cacheEntry.timestamp + (this.SERVER_EXPIRATION_TIME * 1000);

    return now >= expirationTime;
  }

  /**
   * Get cached signed URL if valid with operation awareness
   */
  get(userId, urlPath, operation = 'GET') {
    const key = this.getCacheKey(userId, urlPath, operation);
    const cached = this.cache.get(key);

    if (!cached) {
      console.log(`[SIGNED_URL_CACHE] Cache miss for: ${urlPath} (${operation})`);
      return null;
    }

    if (this.isCompletelyExpired(cached)) {
      console.log(`[SIGNED_URL_CACHE] Cache entry completely expired for: ${urlPath} (${operation})`);
      this.cache.delete(key);
      return null;
    }

    if (this.isExpired(cached)) {
      console.log(`[SIGNED_URL_CACHE] Cache entry near expiration for: ${urlPath} (${operation})`);
      // Return the URL but mark for refresh
      return { ...cached, needsRefresh: true };
    }

    console.log(`[SIGNED_URL_CACHE] Cache hit for: ${urlPath} (${operation})`);
    return cached;
  }

  /**
   * Store signed URL in cache with operation awareness
   */
  set(userId, urlPath, signedUrl, operation = 'GET') {
    const key = this.getCacheKey(userId, urlPath, operation);
    const cacheEntry = {
      signedUrl,
      timestamp: Date.now(),
      userId,
      urlPath,
      operation,
      expiresAt: Date.now() + (this.SERVER_EXPIRATION_TIME * 1000)
    };

    this.cache.set(key, cacheEntry);
    console.log(`[SIGNED_URL_CACHE] Cached ${operation} signed URL for: ${urlPath} (expires at ${new Date(cacheEntry.expiresAt).toISOString()})`);

    return cacheEntry;
  }

  /**
   * Remove specific entry from cache with operation awareness
   */
  delete(userId, urlPath, operation = null) {
    if (operation) {
      const key = this.getCacheKey(userId, urlPath, operation);
      const deleted = this.cache.delete(key);
      if (deleted) {
        console.log(`[SIGNED_URL_CACHE] Removed cached ${operation} entry for: ${urlPath}`);
      }
      return deleted;
    } else {
      // Delete all operations for this URL
      let deletedCount = 0;
      for (const [key, entry] of this.cache.entries()) {
        if (entry.userId === userId && entry.urlPath === urlPath) {
          this.cache.delete(key);
          deletedCount++;
        }
      }
      if (deletedCount > 0) {
        console.log(`[SIGNED_URL_CACHE] Removed ${deletedCount} cached entries for: ${urlPath}`);
      }
      return deletedCount > 0;
    }
  }

  /**
   * Clear all cache entries
   */
  clear() {
    const size = this.cache.size;
    this.cache.clear();
    console.log(`[SIGNED_URL_CACHE] Cleared ${size} cached entries`);
  }

  /**
   * Get cache statistics
   */
  getStats() {
    const now = Date.now();
    let validEntries = 0;
    let expiredEntries = 0;
    let nearExpirationEntries = 0;

    for (const entry of this.cache.values()) {
      if (this.isCompletelyExpired(entry)) {
        expiredEntries++;
      } else if (this.isExpired(entry)) {
        nearExpirationEntries++;
      } else {
        validEntries++;
      }
    }

    return {
      totalEntries: this.cache.size,
      validEntries,
      nearExpirationEntries,
      expiredEntries,
      timestamp: now
    };
  }

  /**
   * Clean up expired entries
   */
  cleanup() {
    const initialSize = this.cache.size;
    let removedCount = 0;

    for (const [key, entry] of this.cache.entries()) {
      if (this.isCompletelyExpired(entry)) {
        this.cache.delete(key);
        removedCount++;
      }
    }

    if (removedCount > 0) {
      console.log(`[SIGNED_URL_CACHE] Cleanup removed ${removedCount} expired entries (${initialSize} -> ${this.cache.size})`);
    }

    return removedCount;
  }

  /**
   * Start periodic cleanup timer
   */
  startCleanupTimer() {
    setInterval(() => {
      this.cleanup();
    }, this.CLEANUP_INTERVAL);
  }
}

// Global cache instance
const signedUrlCache = new SignedUrlCache();

// ===== REQUEST TYPE OPERATION MAPPING =====

/**
 * Mirror server's signedUrlUtilities.js type definitions
 * Maps request types to their corresponding operations (PUT vs GET)
 */
const REQUEST_TYPE_OPERATIONS = {
  // PDF Operations
  'BOOK_ELEMENTS_SOURCE_PDF': 'PUT',
  'BOOK_ELEMENTS_GET_SOURCE_PDF': 'GET',

  // TXT Operations  
  'BOOK_ELEMENTS_SOURCE_TXT': 'PUT',
  'BOOK_ELEMENTS_GET_SOURCE_TXT': 'GET',

  // Image Operations
  'BOOK_ELEMENTS_IMAGE': 'PUT',

  // Process Items
  'BOOK_PROCESS_ITEMS': 'PUT',
  'AUDIO_BOOK_GET_DATA': 'GET',

  // Admin Operations
  'ADMIN_PATH': 'PUT',

  // Voice Files
  'VOICE_FILE': 'PUT',

  // Default folder operations
  'FOLDER': 'GET'
};

/**
 * Get the operation type for a request type
 * @param {string} requestType - The request type
 * @returns {string} The operation type (PUT or GET)
 */
const getOperationForRequestType = (requestType) => {
  const operation = REQUEST_TYPE_OPERATIONS[requestType];
  if (!operation) {
    console.warn(`Unknown request type: ${requestType}, defaulting to GET`);
    return 'GET';
  }
  return operation;
};

// ===== ENHANCED UTILITY FUNCTIONS =====

/**
 * Enhanced version of fetchUserSignedUrlPath with intelligent caching
 * @param {string} userId - The user ID for authentication
 * @param {string} urlPath - The S3 URL to get a signed URL for
 * @param {Object} options - Additional options
 * @param {boolean} options.forceRefresh - Force refresh even if cached
 * @param {boolean} options.backgroundRefresh - Refresh in background if near expiration
 * @returns {Promise<string>} A promise that resolves to the signed URL
 */
export const fetchUserSignedUrlPathV2 = async (userId, urlPath, options = {}) => {
  const {
    forceRefresh = false,
    backgroundRefresh = false,
    operation = 'GET'
  } = options;

  try {

    console.log(`fetchUserSignedUrlPathV2 called with operation: ${operation}`, {
      userId,
      urlPath,
      operation
    });

    let decodedUrlPath = decodeHTMLEntities(urlPath);

    // Check operation-aware cache
    if (!forceRefresh) {
      const cached = signedUrlCache.get(userId, decodedUrlPath, operation);

      if (cached && !cached.needsRefresh) {
        console.log(`[SIGNED_URL_CACHE] Returning cached ${operation} URL`);
        return cached.signedUrl;
      }

      if (cached && cached.needsRefresh && !backgroundRefresh) {
        console.log(`[SIGNED_URL_CACHE] Triggering background refresh for ${operation} operation`);

        // Background refresh
        fetchUserSignedUrlPathV2(userId, urlPath, {
          forceRefresh: true,
          backgroundRefresh: true,
          operation
        }).catch(error => {
          console.error(`[SIGNED_URL_CACHE] Background refresh failed for ${operation}:`, error);
        });

        return cached.signedUrl;
      }
    }

    // Handle edge cases
    if (!decodedUrlPath.startsWith('http://') && !decodedUrlPath.startsWith('https://')) {
      console.warn('EDGE CASE FOUND in fetchSignedURLByFolderV2:', decodedUrlPath);
      return fetchUserSignedUrlPath(userId, urlPath);
    }

    // Fetch new signed URL with operation awareness
    console.log(`[SIGNED_URL_CACHE] Fetching new ${operation} signed URL for: ${decodedUrlPath}`);

    const response = await apiClient.post(
      config.API_GET_SIGNED_URL_BY_RAW_PATH,
      {
        userId: userId,
        url: decodedUrlPath,
        operation: operation  // ← Key addition
      }
    );

    if (response && response.data && response.data.signedUrl) {
      const signedUrl = response.data.signedUrl;
      console.log(`Received ${operation} signed URL:`, signedUrl);

      // Cache with operation awareness
      signedUrlCache.set(userId, decodedUrlPath, signedUrl, operation);

      return signedUrl;
    } else {
      console.error(`Failed to get ${operation} signed URL: Invalid response format`);

      // Fallback to cached version
      const cached = signedUrlCache.get(userId, decodedUrlPath, operation);
      if (cached && !signedUrlCache.isCompletelyExpired(cached)) {
        console.log(`[SIGNED_URL_CACHE] Using cached ${operation} URL as fallback`);
        return cached.signedUrl;
      }

      return null;
    }
  } catch (error) {
    console.error(`Error in fetchUserSignedUrlPathV2 for ${operation}:`, error);

    // Error fallback
    const decodedUrlPath = decodeHTMLEntities(urlPath);
    const cached = signedUrlCache.get(userId, decodedUrlPath, operation);
    if (cached && !signedUrlCache.isCompletelyExpired(cached)) {
      console.log(`[SIGNED_URL_CACHE] Using cached ${operation} URL as error fallback`);
      return cached.signedUrl;
    }

    return null;
  }
};

/**
 * A drop-in replacement for fetchSignedUrlFromAudioBookDataPathUtil that uses the enhanced caching
 * @param {string} userId - The user ID for authentication
 * @param {string} urlPath - The S3 URL to get a signed URL for
 * @param {Object} options - Additional options for caching
 * @returns {Promise<string>} A promise that resolves to the signed URL
 */
export const fetchSignedUrlFromAudioBookDataPathUtilV2 = async (userId, urlPath, options = {}) => {
  try {
    // Check if this is a book-process-items path that needs special handling
    if (urlPath.includes('/book-process-items/') || urlPath.includes('book-process-items/')) {
      // Extract the parts
      let parts = urlPath.split('/book-process-items/');
      let bookUuid = parts[0];
      let fileName = parts[1];

      // If bookUuid contains slashes, extract just the last part
      if (bookUuid.includes('/')) {
        bookUuid = bookUuid.split('/').pop();
      }

      // Construct the full URL via the PathRegistry (single source of truth)
      const fullUrl = await buildS3Url(
        'audioBookProcessItems',
        { userId, bookId: bookUuid },
        fileName
      );

      console.log('Constructed full URL for book process item:', fullUrl);

      // Now use the enhanced function with caching
      return fetchUserSignedUrlPathV2(userId, fullUrl, options);
    }

    // For other paths, just use the enhanced function
    return fetchUserSignedUrlPathV2(userId, urlPath, options);
  } catch (error) {
    console.error('Error in fetchSignedUrlFromAudioBookDataPathUtilV2:', error);
    return null;
  }
};

/**
 * A drop-in replacement for fetchSignedUrlByType that uses the enhanced caching
 * @param {Object} fetchByType - The parameters object
 * @param {Object} options - Additional options for caching
 * @returns {Promise<string>} A promise that resolves to the signed URL
 */
export const fetchSignedUrlByTypeV2 = async (fetchByType, options = {}) => {
  try {
    // Extract the necessary parameters
    const { userId, requestType, folderName, fileName, bookUuid, rootGroup, infoType } = fetchByType;

    console.log('fetchSignedUrlByTypeV2 called with:', { requestType, userId, bookUuid, fileName });

    // Determine operation type from request type
    const operation = getOperationForRequestType(requestType);
    console.log(`Request type ${requestType} maps to ${operation} operation`);

    // Construct a URL based on the request type.
    // Request types with a matching PathRef are resolved via PathRegistry (single
    // source of truth); the rest stay on legacy templates because they either
    // live outside the aai-users namespace (ADMIN_PATH -> aai-core), use dynamic
    // sub-folders with no static template (AUDIO_BOOK_GET_DATA, FOLDER), or
    // target a sub-folder that has no PathRef entry (BOOK_ELEMENTS_IMAGE).
    let url;
    switch (requestType) {
      case 'BOOK_ELEMENTS_SOURCE_PDF':
      case 'BOOK_ELEMENTS_GET_SOURCE_PDF':
        url = await buildS3Url('audioBookSourcePdf', { userId, bookId: bookUuid }, fileName);
        break;
      case 'BOOK_ELEMENTS_SOURCE_TXT':
      case 'BOOK_ELEMENTS_GET_SOURCE_TXT':
        url = await buildS3Url('audioBookSourceTxt', { userId, bookId: bookUuid }, fileName);
        break;
      case 'BOOK_PROCESS_ITEMS':
        url = await buildS3Url('audioBookProcessItems', { userId, bookId: bookUuid }, fileName);
        break;
      case 'VOICE_FILE': {
        const { actorUuid, portfolioUuid, characterUuid, variationUuid } = fetchByType;
        url = await buildS3Url(
          'actVariationVoice',
          {
            userId,
            actorId: actorUuid,
            portfolioId: portfolioUuid,
            characterId: characterUuid,
            variationId: variationUuid,
          },
          fileName
        );
        break;
      }
      // --- Legacy templates (no matching PathRef) ---
      case 'AUDIO_BOOK_GET_DATA':
        // Dynamic folderName — no static PathRef fits this shape
        url = `https://aai-api.s3.us-west-1.amazonaws.com/aai-users/${userId}/audio-books/${bookUuid}/${folderName}/${fileName}`;
        break;
      case 'BOOK_ELEMENTS_IMAGE':
        // book-elements/images/ subfolder — no dedicated PathRef
        url = `https://aai-api.s3.us-west-1.amazonaws.com/aai-users/${userId}/audio-books/${bookUuid}/book-elements/images/${fileName}`;
        break;
      case 'ADMIN_PATH':
        // aai-core root (not aai-users) — outside PathRegistry scope
        url = `https://aai-api.s3.us-west-1.amazonaws.com/aai-core/${rootGroup}/${infoType}/${folderName}/${fileName}`;
        break;
      case 'FOLDER':
      default:
        // Generic pass-through folder — no matching PathRef
        url = `https://aai-api.s3.us-west-1.amazonaws.com/aai-users/${userId}/${folderName}/${fileName}`;
        break;
    }

    // Use enhanced fetchUserSignedUrlPathV2 with operation awareness
    return await fetchUserSignedUrlPathV2(userId, url, {
      ...options,
      operation: operation  // ← Pass the determined operation
    });

  } catch (error) {
    console.error('Error in fetchSignedUrlByTypeV2:', error);
    return null;
  }
};

// ===== CACHE MANAGEMENT FUNCTIONS =====

/**
 * Get cache statistics for monitoring
 * @returns {Object} Cache statistics
 */
export const getSignedUrlCacheStats = () => {
  return signedUrlCache.getStats();
};

/**
 * Clear the signed URL cache
 * @param {string} userId - Optional: clear only entries for specific user
 * @param {string} urlPath - Optional: clear only specific URL
 */
export const clearSignedUrlCache = (userId = null, urlPath = null) => {
  if (userId && urlPath) {
    return signedUrlCache.delete(userId, urlPath);
  } else {
    signedUrlCache.clear();
    return true;
  }
};

/**
 * Force cleanup of expired entries
 * @returns {number} Number of entries removed
 */
export const cleanupSignedUrlCache = () => {
  return signedUrlCache.cleanup();
};

/**
 * ⭐ THE DERIVATIVE LADDER, from `docs/journal/IMAGE_DERIVATIVES_SPEC.md` §3. Long edge in px,
 * each 2× its display width, because a cover rendered at 328 px needs 656 to stay sharp on the
 * screens people read on.
 */
export const DERIVATIVE_SIZES = {
  micro: 112,    // marketplace tables and cards — 34×51 and Semantic `mini`
  thumb: 320,    // entity cards, selector rows, book lists
  card: 640,     // a cover column at 20.5rem
  detail: 1200,  // a book shown large
  hero: 2000,    // article hero banner
};

/**
 * Candidate derivative URLs for an original, largest-confidence first.
 *
 * ⭐ THE KEY IS DERIVED, NEVER STORED (spec §4): `…/cover/x.png` becomes
 * `…/cover/derivatives/x__micro.webp`. A `derivativesJson` property would be a second source of
 * truth for a fact the key already states, and would go stale the first time a backfill ran
 * outside the app.
 *
 * ⚠️ TWO CANDIDATES, because the EXTENSION depends on the source and the client cannot know it.
 * `generateImageDerivatives.js` writes WebP unless the source has an alpha channel that is
 * actually used — a genuinely transparent logo stays PNG, since flattening one onto white is a
 * visual bug rather than an optimisation. Covers are WebP; a logo may not be. ⓘ So callers try
 * these in order and fall back to the original, which is also what makes this safe to ship
 * before every image has been backfilled.
 *
 * @param {string} originalUrl a full S3 URL to the ORIGINAL
 * @param {string} size one of DERIVATIVE_SIZES
 * @returns {string[]} candidate URLs; empty if the input is not a derivable URL
 */
export const derivativeCandidates = (originalUrl, size = 'micro') => {
  const str = String(originalUrl || '').trim();
  if (!str || !DERIVATIVE_SIZES[size]) return [];
  const q = str.indexOf('?');
  const bare = q === -1 ? str : str.slice(0, q);          // ⓘ drop any existing signature
  const slash = bare.lastIndexOf('/');
  if (slash === -1) return [];
  const dir = bare.slice(0, slash);
  const file = bare.slice(slash + 1);
  if (!file || dir.endsWith('/derivatives')) return [];   // ⛔ never derive from a derivative
  const dot = file.lastIndexOf('.');
  const base = dot === -1 ? file : file.slice(0, dot);
  // ⓘ Double underscore: basenames already contain single ones, so `x__micro` stays parseable.
  return ['webp', 'png'].map((e) => dir + '/derivatives/' + base + '__' + size + '.' + e);
};

/**
 * Sign the smallest available derivative of an image, falling back to the original.
 *
 * ⭐ WHY THIS IS THE WHOLE WIN. Measured 2026-10-22: book covers average 2,839 KB and the
 * marketplace renders them at 34×51 px, so a page of ten fetched **23.9 MB**. The `micro`
 * derivative is 3,072 bytes, which makes the same page **30 KB**.
 *
 * ⚠️ It signs the DERIVATIVE key rather than the original — same number of signing calls, and
 * those were already parallel and cached for an hour by `signedUrlCache`, so round-trips were
 * never the bottleneck. The bytes were.
 *
 * ⓘ Returns `{ url, isDerivative }` so a caller can keep the original as an onError fallback
 * without signing it twice.
 */
export const signDerivativeForUser = async (userId, originalUrl, size = 'micro') => {
  if (!originalUrl || !userId) return { url: '', isDerivative: false };
  for (const candidate of derivativeCandidates(originalUrl, size)) {
    try {
      const signed = await signS3UrlForUser(userId, candidate, { logErrors: false });
      if (signed) return { url: signed, isDerivative: true, original: originalUrl };
    } catch (e) {
      /* try the next extension */
    }
  }
  // ⛔ No derivative signed — the original still works, just heavier. Never show nothing.
  const fallback = await signS3UrlForUser(userId, originalUrl);
  return { url: fallback, isDerivative: false, original: originalUrl };
};

/**
 * Preload signed URLs for better performance
 * @param {Array} urlRequests - Array of {userId, urlPath} objects
 * @returns {Promise<Array>} Array of signed URLs
 */
export const preloadSignedUrls = async (urlRequests) => {
  console.log(`[SIGNED_URL_CACHE] Preloading ${urlRequests.length} signed URLs`);

  const promises = urlRequests.map(({ userId, urlPath }) =>
    fetchUserSignedUrlPathV2(userId, urlPath)
  );

  try {
    const results = await Promise.allSettled(promises);
    const successful = results.filter(result => result.status === 'fulfilled').length;
    console.log(`[SIGNED_URL_CACHE] Preloaded ${successful}/${urlRequests.length} signed URLs successfully`);

    return results.map(result =>
      result.status === 'fulfilled' ? result.value : null
    );
  } catch (error) {
    console.error('[SIGNED_URL_CACHE] Error during preload:', error);
    return [];
  }
};

// ===== UTILITY FUNCTIONS =====

// Keep the original decodeHTMLEntities function for compatibility
function decodeHTMLEntities(text) {
  if (!text) return text;

  const entities = {
    '&#x2F;': '/',
    '&amp;': '&',
    '&#x3A;': ':'
    // Add more entities if needed
  };

  return text.replace(/&#x2F;|&amp;|&#x3A;/g, match => entities[match]);
}
/**
 * Signs an S3 URL for a specific user
 * @param {string} userId - The user ID to sign the URL for
 * @param {string} s3Url - The S3 URL to sign
 * @param {Object} options - Optional configuration
 * @param {boolean} options.logErrors - Whether to log errors (default: true)
 * @returns {Promise<string>} - The signed URL or empty string if failed
 */
const signS3UrlForUser = async (userId, s3Url, options = {}) => {
  const { logErrors = true } = options;

  // Early return for invalid URLs
  if (!s3Url || s3Url === 'pending-recording') {
    return '';
  }

  // Validate required parameters
  if (!userId) {
    if (logErrors) {
      console.error('signS3UrlForUser: userId is required');
    }
    return '';
  }

  try {
    const signedUrl = await fetchUserSignedUrlPathV2(userId, s3Url);
    return signedUrl || '';
  } catch (error) {
    if (logErrors) {
      console.error('Error signing S3 URL:', error);
    }
    return '';
  }
};

/**
 * Alternative version with loading state callback for components that need it
 * @param {string} userId - The user ID to sign the URL for
 * @param {string} s3Url - The S3 URL to sign
 * @param {Function} onLoadingChange - Callback for loading state changes
 * @param {Object} options - Optional configuration
 * @returns {Promise<string>} - The signed URL or empty string if failed
 */
const signS3UrlWithLoading = async (userId, s3Url, onLoadingChange, options = {}) => {
  const { logErrors = true } = options;

  if (!s3Url || s3Url === 'pending-recording') {
    return '';
  }

  if (!userId) {
    if (logErrors) {
      console.error('signS3UrlWithLoading: userId is required');
    }
    return '';
  }

  // Notify loading start
  if (typeof onLoadingChange === 'function') {
    onLoadingChange(true);
  }

  try {
    const signedUrl = await fetchUserSignedUrlPathV2(userId, s3Url);
    return signedUrl || '';
  } catch (error) {
    if (logErrors) {
      console.error('Error signing S3 URL:', error);
    }
    return '';
  } finally {
    // Notify loading end
    if (typeof onLoadingChange === 'function') {
      onLoadingChange(false);
    }
  }
};

// Export the original functions from the original file for compatibility
export {
  fetchUserSignedUrlPathV2 as fetchUserSignedUrlPath,
  fetchSignedUrlFromAudioBookDataPathUtilV2 as fetchSignedUrlFromAudioBookDataPathUtil,
  fetchSignedUrlByTypeV2 as fetchSignedUrlByType,
  signS3UrlForUser, signS3UrlWithLoading
};
